import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box } from '@material-ui/core';

import Modal from 'features/app/components/Modal';
import { useTranslation } from 'hooks/useTranslation';
import { InputField } from 'shared/forms';
import useValidation from 'hooks/useValidation';
import { SelectInputField } from 'features/app/components/SelectInputField';
import BaseButton from 'features/app/components/BaseButton';
import { fetchRoleItems } from 'features/users/utils/fetchRoleItems';
import { useMessage } from 'features/message/hooks/useMessage';
import { ErrorUtils } from 'utils';
import { editUserProfileRequest } from 'features/users/api';
import { mapSelectOutput } from 'features/app/utils/mapSelectOutput';
import { parseFieldErrors } from 'utils/error-utils';
import { PartnerUser } from 'features/users/ts/PartnerUser';
import { confirmDialog } from 'features/app/utils/confirmDialog';

import { useStyles } from './styles';
import { useSelector } from 'store';
import { editProfileRequest } from 'features/user/api';
import { profileSuccess } from 'features/user/redux/actions';
import { useDispatch } from 'react-redux';

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (user: PartnerUser) => Promise<unknown>;
  user: PartnerUser;
  // disableRoles?: boolean;
}

interface EditPartnerUserFormState extends PartnerUser {
  old_password: string;
  new_password: string;
  repeat_password: string;
}

function filterNulls<T>(object: T): T {
  return Object.entries(object).reduce((acc, [key, value]) => {
    if (value === null) {
      return acc;
    } else return { ...acc, [key]: value };
  }, {}) as T;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  onClose,
  onSubmit: onModalSubmit,
  open,
  user,
  // disableRoles = false,
}) => {
  const { t } = useTranslation('users');
  const { required, email, password, isEqual } = useValidation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { showErrorMessage, showSuccessMessage } = useMessage();
  const currentUserId = useSelector(state => state.user.user?.id);
  const isEditingHimself = React.useMemo(() => user.id === currentUserId, [user, currentUserId]);

  const mappedUser = React.useMemo(() => {
    return { ...user, roles: user.roles.map(role => ({ label: role.readable_name, value: role.id })) };
  }, [user]) as unknown as EditPartnerUserFormState;

  const form = useForm<EditPartnerUserFormState>({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: mappedUser,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    setError,
    watch,
  } = form;

  const newPassword = watch('new_password');
  const repeatPassword = watch('repeat_password');
  const showOldPassword = isEditingHimself && Boolean(newPassword || repeatPassword);

  const onSubmit = React.useCallback(
    async (values: EditPartnerUserFormState) => {
      try {
        const profile = filterNulls(mapSelectOutput(['roles'], values));
        const payload = {
          ...profile,
          repeat_password: undefined,
          new_password: profile.new_password || undefined,
          old_password: profile.new_password && isEditingHimself ? profile.old_password : undefined,
        };

        let response;

        if (isEditingHimself) {
          response = await editProfileRequest(payload);
          dispatch(profileSuccess(response.data));
        } else {
          response = await editUserProfileRequest(user.id, payload);
        }
        const { data } = response;

        await onModalSubmit?.(data);
        showSuccessMessage(t('userMessages.profileUpdated'));
        onClose();
      } catch (err) {
        const parsedError = ErrorUtils.convertError(err);

        parseFieldErrors<EditPartnerUserFormState>(parsedError).forEach(({ field, ...error }) =>
          setError(field, error)
        );
        showErrorMessage(parsedError);
      }
    },
    [onModalSubmit, onClose, showSuccessMessage, showErrorMessage, setError, t, user, isEditingHimself, dispatch]
  );

  const handleClose = React.useCallback(async () => {
    let shouldClose: undefined | boolean = true;

    if (isDirty) {
      shouldClose = await confirmDialog({ title: t('formLeavingPrompt', { ns: 'common' }) });
    }

    if (shouldClose) {
      onClose();
    }
  }, [onClose, t, isDirty]);

  return (
    <Modal open={open} onClose={handleClose} title={t('editProfile.title')} paperClass={classes.container}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          <InputField
            name="email"
            fullWidth
            placeholder={t('inputs.emailPlaceholder', { ns: 'common' })}
            label={t('email')}
            required
            type="email"
            rules={{
              validate: {
                ...required,
                ...email,
              },
            }}
          />
          <Box display="flex" justifyContent="space-between" marginTop={6}>
            <Box flex={0.48}>
              <InputField
                name="first_name"
                fullWidth
                placeholder={t('inputs.enter', { ns: 'common' })}
                label={t('editProfile.firstName')}
                required
                type="text"
                rules={{
                  validate: {
                    ...required,
                  },
                }}
              />
            </Box>
            <Box flex={0.48}>
              <InputField
                name="last_name"
                fullWidth
                placeholder={t('inputs.enter', { ns: 'common' })}
                label={t('editProfile.lastName')}
                required
                type="text"
                rules={{
                  validate: {
                    ...required,
                  },
                }}
              />
            </Box>
          </Box>

          <Box marginTop={6}>
            <SelectInputField
              name="roles"
              label={t('editProfile.changeRole')}
              isAsync
              fetchItems={fetchRoleItems}
              isDisabled={isEditingHimself}
              /**
               * Made as multiselect for making system extendable to future changing of role system.
               */
              isMulti
              maxLength={1}
              rules={{
                validate: {
                  ...required,
                },
              }}
            />
          </Box>
          {showOldPassword ? (
            <Box marginTop={6}>
              <InputField
                name="old_password"
                fullWidth
                placeholder={t('inputs.enter', { ns: 'common' })}
                label={t('editProfile.oldPasswordPlaceholder')}
                autoComplete="current-password"
                type="password"
                rules={{
                  validate: {
                    ...required,
                  },
                }}
              />
            </Box>
          ) : null}
          <Box marginTop={6}>
            <InputField
              name="new_password"
              fullWidth
              placeholder={t('inputs.enter', { ns: 'common' })}
              label={t('editProfile.passwordPlaceholder')}
              autoComplete="new-password"
              type="password"
              rules={{
                validate: {
                  ...password,
                },
              }}
            />
          </Box>
          <Box marginTop={6}>
            <InputField
              name="repeat_password"
              fullWidth
              placeholder={t('inputs.enter', { ns: 'common' })}
              label={t('editProfile.repeatPasswordPlaceholder')}
              type="password"
              rules={{
                validate: {
                  ...password,
                  ...(newPassword ? { equal: isEqual(newPassword) } : {}),
                },
              }}
            />
          </Box>
          <Box className={classes.buttonsContainer}>
            <Box width="35%" display="flex">
              <BaseButton variant="text" className={classes.cancelButton} onClick={handleClose}>
                {t('cancel', { ns: 'common' })}
              </BaseButton>
              <BaseButton className={classes.saveButton} type="submit" isLoading={isSubmitting}>
                {t('save', { ns: 'common' })}
              </BaseButton>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Modal>
  );
};
